import { z } from 'zod';
import {
    FirecrawlCaseStudyInfoSchema,
    FirecrawlCompanyInfo,
    FirecrawlLocation,
    FirecrawlServiceInfo,
    FirecrawlSiteMap,
    FirecrawlType,
} from '../validators/CompanyImport';
import { FirecrawlProductSeries } from '../validators/FirecrawlTypes';

export enum CompanyImportType {
    LinkedIn = 'linkedIn',
    Firecrawl = 'firecrawl',
}

export type CompanyImportContentToAnalyze = {
    url: string;
    content: string;
};

export type WebsiteProcessingItem = {
    url: string;
    content: any;
    firecrawlTypes: FirecrawlType[];
    locations: FirecrawlLocation[];
    services: FirecrawlServiceInfo[];
    productSeries: FirecrawlProductSeries[];
    applicationTags: string[];
    caseStudies: z.infer<typeof FirecrawlCaseStudyInfoSchema>[];
    updatedAt: string;
};

export const HeroImageCandidates = z.object({
    heroUrl: z.string().catch('').describe('Best option for hero image or video'),
    heroCandidateUrls: z
        .array(z.object({ url: z.string() }))
        .catch([])
        .describe('URLs of candidate images or videos'),
});

export const HeroImageProcessing = z.object({
    heroImageCandidates: HeroImageCandidates,
    manufacturerPreviousCoverImage: z.string().optional(),
});
export type HeroImageProcessing = z.infer<typeof HeroImageProcessing>;
export type HeroImageCandidates = z.infer<typeof HeroImageCandidates>;

export type CompanyImport<T extends CompanyImportType = CompanyImportType> = {
    id: string;
    name: string;
    url: string;
    manufacturer?: string;
    type: T;
    status: 'pending' | 'imported' | 'archived' | 'ignored' | 'failed';
    data?: CompanyImportData<T>;
    heroImageProcessing?: HeroImageProcessing;
    websiteProcessing?: WebsiteProcessingItem[];
    siteMap?: FirecrawlSiteMap;
    notes?: string;
    imported?: string;
    createdAt: string;
};

export type CompanyImportData<T extends CompanyImportType = CompanyImportType> = T extends CompanyImportType.Firecrawl
    ? FirecrawlCompanyInfo
    : T extends CompanyImportType.LinkedIn
      ? LinkedInData
      : never;

export type CompanyImportSimilarCompany = {
    name: string;
    link: string;
    industry: string;
};

export type LinkedInData = {
    name: string;
    description: string;
    similar_companies: CompanyImportSimilarCompany[];
    profile_pic_url: string;
    background_cover_image_url: string;
    [key: string]: any;
};

export class DuplicateCompanyNameError extends Error {
    duplicateName: string;
    constructor(message: string, duplicateName: string) {
        super(message);
        this.duplicateName = duplicateName;
    }
}

export enum CompanyImportsSimilarCompaniesStatus {
    ToDo = 'to-do',
    Scheduled = 'scheduled',
    Completed = 'completed',
    Failed = 'failed',
    Duplicate = 'duplicate',
    Irrelevant = 'irrelevant',
    PreviouslyProcessed = 'previously-processed',
}
